/**
 * «Stage»: bersagli, piantina e altri file di un esercizio di tiro.
 *
 * **Una sezione sola per il ProgramBuilder e per la libreria drill.** Erano
 * due: nel programma c'era la tabella dei bersagli, nella libreria solo il
 * disegno — da cui «non vedo nella configurazione del drill i bersagli» e
 * «i due designer devono comportarsi uguali». Qui c'è tutto, una volta.
 *
 * La **tabella dei bersagli** è la cosa che il tiratore compila: una riga di
 * punteggio per ogni T. Per questo ogni riga mostra il suo numero (`T1`,
 * `T2-T4`): è il filo fra ciò che il coach configura e ciò che il tiratore si
 * trova davanti, e senza il numero quel filo era invisibile. La tabella si
 * scrive a mano — il caso di chi carica la foto di una piantina senza usare
 * il designer — oppure la produce il disegno.
 *
 * Gli **altri file** sono ciò che non è la piantina: la foto del bersaglio,
 * un dettaglio. Prima non avevano posto.
 *
 * Spec: docs/shooting/15-seconda-sessione-federico.md
 */
import { useState } from "react";
import {
	ImagePlus,
	Loader2,
	PencilRuler,
	Plus,
	Trash2,
	X,
	ZoomIn,
} from "lucide-react";
import { toast } from "sonner";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from "../ui/select";
import { API_URL } from "../../lib/utils";
import { SHOOTING_TARGET_TYPES } from "../../lib/programBuilderUtils";
import { defaultHitsRequired, findElement } from "./stageElements";
import { StageDesignerDialog, StagePreview } from "./StageDesignerDialog";
import { ImageZoomDialog } from "./ImageZoomDialog";
import { Section } from "./formPrimitives";
import { expandTargetRows, targetRowLabels } from "./shootingPlan";

/** L'URL assoluto di un'immagine caricata: il backend serve `/api/uploads/…`. */
export const stageImageSrc = (url) => {
	const safe = typeof url === "string" ? url.trim() : "";
	if (!safe) return null;
	return safe.startsWith("http") ? safe : `${API_URL}${safe}`;
};

/**
 * Carica un'immagine su `/shooting/stage-image` e restituisce l'URL.
 *
 * FormData a mano perché `fetchAPI` imposta un Content-Type JSON. Era
 * copiata in due file; ora è qui e basta.
 */
export const uploadShootingImage = async (file, t) => {
	const body = new FormData();
	body.append("file", file);
	const response = await fetch(`${API_URL}/api/shooting/stage-image`, {
		method: "POST",
		credentials: "include",
		headers: {
			Authorization: `Bearer ${localStorage.getItem("session_token") || ""}`,
		},
		body,
	});
	if (!response.ok) {
		const detail = await response.json().catch(() => ({}));
		throw new Error(detail.detail || t("pbShootUploadFailed"));
	}
	const data = await response.json();
	return data.image_url;
};

// Niente colonna «Qta»: ogni sagoma è una riga, perché ha la sua distanza e
// soprattutto il suo punteggio sulla scheda del tiratore.
const GRID = "grid-cols-[2.6rem,1fr,4.2rem,4.2rem,2rem]";

export function StageSetupSection({
	shooting = {},
	onChange,
	t,
	discipline = "ipsc",
	title,
	hint,
}) {
	// Il disegno vive in una finestra a sé: dentro una modale sarebbe un
	// contenitore scrollabile dentro un altro.
	const [designerOpen, setDesignerOpen] = useState(false);
	const [uploading, setUploading] = useState(null); // "stage" | "attachment"
	const [zoom, setZoom] = useState(null); // { src, title }

	// Le righe salvate con una quantità (`3× IPSC`) si aprono in righe singole:
	// la prima modifica le riscrive così, e la scheda non cambia numeri.
	const targets = expandTargetRows(shooting.targets);
	const attachments = Array.isArray(shooting.attachments)
		? shooting.attachments
		: [];
	const labels = targetRowLabels(targets);
	const hasDrawnStage =
		(shooting.stage_layout || []).length > 0 ||
		targets.some((target) => target.x_m !== null && target.x_m !== undefined);
	const stageSrc = stageImageSrc(shooting.stage_image_url);

	const updateTarget = (index, changes) =>
		onChange({
			targets: targets.map((target, i) =>
				i === index ? { ...target, ...changes } : target,
			),
		});

	const upload = async (file, kind) => {
		if (!file) return;
		setUploading(kind);
		try {
			const url = await uploadShootingImage(file, t);
			if (kind === "stage") onChange({ stage_image_url: url });
			else
				onChange({
					attachments: [...attachments, { url, label: "", kind: "image" }],
				});
		} catch (error) {
			toast.error(`${t("error")}: ${error.message}`);
		} finally {
			setUploading(null);
		}
	};

	const typeLabel = (value) => {
		const entry = SHOOTING_TARGET_TYPES.find((item) => item.value === value);
		return (entry?.labelKey && t(entry.labelKey)) || entry?.label || findElement(value)?.label || value;
	};
	// Un tipo salvato che la tabella non elenca (legacy) resta leggibile.
	const typeOptions = SHOOTING_TARGET_TYPES.map((item) => item.value);

	return (
		<Section
			title={title || t("pbShootStageSection")}
			hint={hint || t("pbShootTargetTableHint")}
			action={
				<Button
					type="button"
					size="sm"
					variant="outline"
					className="rounded-sm h-7 shrink-0"
					onClick={() =>
						onChange({
							targets: [
								...targets,
								{ count: 1, target_type: "ipsc", distance_m: null },
							],
						})
					}
				>
					<Plus className="w-3.5 h-3.5 mr-1" />
					{t("pbShootTarget")}
				</Button>
			}
		>
			{/* ── Tabella dei bersagli ── */}
			{targets.length === 0 ? (
				<p className="text-xs text-muted-foreground">{t("pbShootNoTarget")}</p>
			) : (
				<div className={`grid ${GRID} gap-1.5 px-1`}>
					{[
						"#",
						t("pbShootColTarget"),
						t("pbShootColDistance"),
						t("pbShootColHits"),
						"",
					].map((head, i) => (
						<span
							key={`${head}_${i}`}
							className="text-[10px] uppercase tracking-wider text-muted-foreground"
							title={i === 3 ? t("pbShootColHitsHint") : undefined}
						>
							{head}
						</span>
					))}
				</div>
			)}
			{targets.map((target, index) => {
				const type = target.target_type || "ipsc";
				const isNoShoot = defaultHitsRequired(type) === 0;
				return (
					<div
						key={`target_${index}`}
						className={`grid ${GRID} gap-1.5 items-center`}
					>
						{/* Il numero della scheda: è ciò che il tiratore vedrà. */}
						<span
							className={`text-[11px] font-semibold text-center tabular-nums ${
								isNoShoot ? "text-muted-foreground" : "text-volt"
							}`}
						>
							{labels[index]}
						</span>
						<Select
							value={type}
							onValueChange={(v) => updateTarget(index, { target_type: v })}
						>
							<SelectTrigger className="bg-background border-transparent rounded-sm text-xs h-10">
								<SelectValue />
							</SelectTrigger>
							<SelectContent className="bg-card border-border">
								{!typeOptions.includes(type) && (
									<SelectItem value={type}>{typeLabel(type)}</SelectItem>
								)}
								{SHOOTING_TARGET_TYPES.map((item) => (
									<SelectItem key={item.value} value={item.value}>
										{typeLabel(item.value)}
									</SelectItem>
								))}
							</SelectContent>
						</Select>
						<Input
							type="number"
							step="0.5"
							inputMode="decimal"
							value={target.distance_m ?? ""}
							onChange={(e) =>
								updateTarget(index, {
									distance_m: e.target.value ? parseFloat(e.target.value) : null,
								})
							}
							className="bg-background border-transparent rounded-sm text-xs px-2 h-10"
							placeholder="m"
						/>
						{/* Colpi che contano su questo bersaglio. Vuoto = il
						    default del regolamento (carta 2, metallo 1). */}
						<Input
							type="number"
							min="0"
							inputMode="numeric"
							disabled={isNoShoot}
							value={isNoShoot ? "" : (target.hits_required ?? "")}
							onChange={(e) =>
								updateTarget(index, {
									hits_required: e.target.value
										? parseInt(e.target.value, 10)
										: null,
								})
							}
							className="bg-background border-transparent rounded-sm text-xs px-2 h-10"
							placeholder={isNoShoot ? "—" : String(defaultHitsRequired(type))}
							title={t("pbShootColHitsHint")}
						/>
						<Button
							type="button"
							variant="ghost"
							size="icon"
							className="rounded-sm hover:text-red-500 h-8 w-8"
							onClick={() =>
								onChange({ targets: targets.filter((_, i) => i !== index) })
							}
						>
							<Trash2 className="w-3.5 h-3.5" />
						</Button>
					</div>
				);
			})}

			{/* ── Piantina: disegnata, fotografata, o entrambe ── */}
			{stageSrc && (
				<div className="relative">
					<button
						type="button"
						className="w-full"
						onClick={() => setZoom({ src: stageSrc, title: t("pbShootStageMap") })}
						title={t("pbShootZoom")}
					>
						<img
							src={stageSrc}
							alt={t("pbShootStageMap")}
							className="w-full max-h-40 object-contain rounded-sm bg-background"
						/>
					</button>
					<span className="absolute bottom-1 right-1 rounded-sm bg-background/80 p-1 text-muted-foreground pointer-events-none">
						<ZoomIn className="w-3.5 h-3.5" />
					</span>
					<Button
						type="button"
						variant="ghost"
						size="icon"
						className="absolute top-1 right-1 h-7 w-7 rounded-sm bg-background/80 hover:text-red-500"
						onClick={() => onChange({ stage_image_url: null })}
						title={t("remove")}
					>
						<X className="w-4 h-4" />
					</Button>
				</div>
			)}
			{hasDrawnStage && (
				<StagePreview prescription={shooting} onOpen={() => setDesignerOpen(true)} />
			)}
			<div className="grid grid-cols-2 gap-1.5">
				<Button
					type="button"
					variant="outline"
					className={`rounded-sm text-xs ${stageSrc || hasDrawnStage ? "h-9" : "h-14 flex-col gap-1"}`}
					onClick={() => setDesignerOpen(true)}
				>
					<PencilRuler className="w-4 h-4 mr-1" />
					{hasDrawnStage ? t("edit") : t("pbShootDrawStage")}
				</Button>
				{stageSrc ? (
					<Button
						type="button"
						variant="ghost"
						className="rounded-sm text-xs h-9 text-muted-foreground hover:text-red-500"
						onClick={() => onChange({ stage_layout: [], stage_annotations: [] })}
						disabled={!hasDrawnStage}
						title={t("pbShootCloseDrawing")}
					>
						<Trash2 className="w-3.5 h-3.5 mr-1" />
						{t("pbShootCloseDrawing")}
					</Button>
				) : (
					<label
						className={`flex items-center justify-center gap-1 rounded-sm border border-dashed border-border text-xs text-muted-foreground cursor-pointer hover:border-volt/50 transition-colors ${
							hasDrawnStage ? "h-9" : "h-14 flex-col"
						}`}
					>
						{uploading === "stage" ? (
							<Loader2 className="w-4 h-4 animate-spin" />
						) : (
							<>
								<ImagePlus className="w-4 h-4" />
								{t("pbShootUploadImage")}
							</>
						)}
						<input
							type="file"
							accept="image/*"
							className="hidden"
							disabled={!!uploading}
							onChange={(event) => {
								const file = event.target.files?.[0];
								event.target.value = "";
								upload(file, "stage");
							}}
						/>
					</label>
				)}
			</div>

			{/* ── Altri file ── */}
			<div className="space-y-1.5 pt-1">
				<div className="flex items-center justify-between gap-2">
					<div className="min-w-0">
						<p className="text-[10px] uppercase tracking-wider text-muted-foreground">
							{t("pbShootAttachments")}
						</p>
						<p className="text-[10px] text-muted-foreground/80 leading-snug">
							{t("pbShootAttachmentsHint")}
						</p>
					</div>
					<label className="flex items-center gap-1 h-7 px-2 rounded-sm border border-border text-[11px] text-muted-foreground cursor-pointer hover:text-foreground hover:border-volt/40 shrink-0">
						{uploading === "attachment" ? (
							<Loader2 className="w-3.5 h-3.5 animate-spin" />
						) : (
							<>
								<ImagePlus className="w-3.5 h-3.5" />
								{t("pbShootAddAttachment")}
							</>
						)}
						<input
							type="file"
							accept="image/*"
							className="hidden"
							disabled={!!uploading}
							onChange={(event) => {
								const file = event.target.files?.[0];
								event.target.value = "";
								upload(file, "attachment");
							}}
						/>
					</label>
				</div>
				{attachments.length > 0 && (
					<div className="grid grid-cols-2 sm:grid-cols-3 gap-1.5">
						{attachments.map((item, index) => {
							const src = stageImageSrc(item.url);
							return (
								<div
									key={`${item.url}_${index}`}
									className="relative rounded-sm border border-border bg-background overflow-hidden"
								>
									<button
										type="button"
										className="w-full"
										onClick={() => setZoom({ src, title: item.label || t("pbShootAttachments") })}
										title={t("pbShootZoom")}
									>
										<img
											src={src}
											alt={item.label || ""}
											className="w-full h-20 object-contain bg-white"
										/>
									</button>
									<Input
										value={item.label || ""}
										onChange={(e) =>
											onChange({
												attachments: attachments.map((a, i) =>
													i === index ? { ...a, label: e.target.value } : a,
												),
											})
										}
										className="bg-transparent border-0 border-t border-border rounded-none text-[11px] h-8 px-2"
										placeholder={t("pbShootAttachmentLabelPlaceholder")}
									/>
									<Button
										type="button"
										variant="ghost"
										size="icon"
										className="absolute top-0.5 right-0.5 h-6 w-6 rounded-sm bg-background/80 hover:text-red-500"
										onClick={() =>
											onChange({
												attachments: attachments.filter((_, i) => i !== index),
											})
										}
									>
										<X className="w-3.5 h-3.5" />
									</Button>
								</div>
							);
						})}
					</div>
				)}
			</div>

			<StageDesignerDialog
				open={designerOpen}
				onOpenChange={setDesignerOpen}
				prescription={shooting}
				plannedRounds={shooting.rounds_planned}
				discipline={discipline}
				onSave={(next) => onChange(next)}
				// Il disegno diventa anche un'immagine: è ciò che l'atleta può
				// ingrandire e stampare per andare al campo.
				onExportPng={async (blob) => {
					await upload(
						new File([blob], "piantina.png", { type: "image/png" }),
						"stage",
					);
					toast.success(t("pbShootImageGenerated"));
				}}
			/>
			<ImageZoomDialog
				open={!!zoom}
				onOpenChange={(open) => !open && setZoom(null)}
				src={zoom?.src}
				title={zoom?.title}
				alt={zoom?.title}
			/>
		</Section>
	);
}
